import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Clock, CheckCircle2, ListChecks, Plus, ChevronRight } from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import SupStatusBadge from "../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupEmptyState from "../../../components/general/SupportSharedFiles/SupEmptyState";
import SupLoader from "../../../components/general/SupportSharedFiles/SupLoader";
import "../../../assets/css/general/Support/supshare.css";
import "../../../assets/css/customer/customerSupport/cussupportdashboard.css";


const CusSupportDashboard = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStats = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/support/dashboard-stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setStats({
          total: response.data.total || 0,
          pending: response.data.pending || 0,
          resolved: response.data.resolved || 0,
        });
        setRecent(response.data.recent || []);
        setError("");
      })
      .catch((err) => {
        console.error("API ERROR:", err.response?.data || err.message);
        setError("Failed to load support dashboard.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStats();
  }, [authToken]);

  const getTicketTitle = (ticket) => {
    return ticket.jjbooking?.Jjpackage?.package_name || ticket.category || "Support Ticket";
  };

  if (loading) {
    return (
      <div className="cus-sd-page">
        <SupLoader label="Loading your support dashboard..." />
      </div>
    );
  }

  return (
    <div className="cus-sd-page">
      {/* Header */}
      <div className="cus-sd-header">
        <div>
          <h2 className="cus-sd-heading">Help &amp; Support</h2>
          <p className="cus-sd-subtext">Track and manage your support tickets</p>
        </div>
        <button
          className="cus-sd-create-btn"
          onClick={() => navigate("/customer/support/create")}
        >
          <Plus size={16} /> New Ticket
        </button>
      </div>

      {error && <div className="sup-error-banner">{error}</div>}

      {/* Stat Cards */}
      <div className="cus-sd-stats">
        <div className="cus-sd-stat-card">
          <div className="cus-sd-stat-icon cus-sd-stat-icon--total">
            <ListChecks size={20} />
          </div>
          <div>
            <p className="cus-sd-stat-value">{stats.total}</p>
            <p className="cus-sd-stat-label">Total Tickets</p>
          </div>
        </div>

        <div className="cus-sd-stat-card">
          <div className="cus-sd-stat-icon cus-sd-stat-icon--pending">
            <Clock size={20} />
          </div>
          <div>
            <p className="cus-sd-stat-value">{stats.pending}</p>
            <p className="cus-sd-stat-label">Pending Tickets</p>
          </div>
        </div>

        <div className="cus-sd-stat-card">
          <div className="cus-sd-stat-icon cus-sd-stat-icon--resolved">
            <CheckCircle2 size={20} />
          </div>
          <div>
            <p className="cus-sd-stat-value">{stats.resolved}</p>
            <p className="cus-sd-stat-label">Resolved Tickets</p>
          </div>
        </div>
      </div>

      {/* Recent Tickets */}
      <div className="cus-sd-recent">
        <div className="cus-sd-recent__header">
          <h3 className="cus-sd-recent__title">Recent Tickets</h3>
          <button
            className="cus-sd-viewall-btn"
            onClick={() => navigate("/customer/support/tickets")}
          >
            View All <ChevronRight size={14} />
          </button>
        </div>

        {recent.length === 0 ? (
          <SupEmptyState
            icon={LifeBuoy}
            title="No support tickets yet"
            subtitle="Create a ticket if you face any issue with your booking"
          />
        ) : (
          <div className="cus-sd-recent__list">
            {recent.map((ticket) => (
              <div
                key={ticket.id}
                className="cus-sd-recent__item"
                onClick={() => navigate(`/customer/support/tickets/${ticket.id}`)}
               >
                <div className="cus-sd-recent__left">
                  <span className="cus-sd-recent__id">#{ticket.id}</span>
                  <div>
                    <p className="cus-sd-recent__name">{getTicketTitle(ticket)}</p>
                    <p className="cus-sd-recent__category">{ticket.category}</p>
                  </div>
                </div>
                <SupStatusBadge status={ticket.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CusSupportDashboard;